import React from "react";

// Primitives only: the dependency goes into the ref's `useCallback`, so a value that is a fresh
// object every render (a `ReactNode`) would re-create the observer each time. Pass the text, not the node.
type ClipDependency = string | number | boolean | null | undefined;

/**
 * Tracks whether an element's text is cut off by `text-overflow: ellipsis`, so a tooltip carrying
 * the full text can be shown only when it is actually needed.
 *
 * A ResizeObserver rather than a window `resize` listener: for a fluid table column the width also
 * changes without the window moving — a side panel opening, the vertical scrollbar appearing once
 * the rows overflow, or a page-size change redistributing the columns.
 */
export const useIsClipped = (dependency: ClipDependency) => {
    const [clipped, setClipped] = React.useState(false);

    // A callback ref (with its React 19 cleanup return) re-attaches when the DOM node itself
    // changes, not only when `dependency` does.
    const ref = React.useCallback(
        (element: HTMLElement | null) => {
            if (!element) {
                setClipped(false);
                return;
            }

            const updateClipped = () => setClipped(element.scrollWidth > element.clientWidth);

            updateClipped();

            const resizeObserver = new ResizeObserver(updateClipped);
            resizeObserver.observe(element);

            return () => resizeObserver.disconnect();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- re-measures when the text changes, not when it is read
        [dependency],
    );

    return { ref, clipped };
};
